import { AccessControl } from '@components/layout'
import {
  ChevronDown,
  CircleCheck,
  Info,
  OctagonX,
  TriangleAlert
} from 'lucide-react'
import { ComponentType, CSSProperties, ReactNode, useState } from 'react'

import { cn } from '@/lib/utils'

type AlertType = 'success' | 'info' | 'warning' | 'error'

interface CollapsibleAlertProps {
  type?: AlertType
  showIcon?: boolean
  icon?: ReactNode
  style?: CSSProperties
  className?: string
  label: ReactNode
  description: ReactNode
  /** @deprecated kept for API compatibility; tone now derives from `type`. */
  borderColor?: `#${string}`
  isExpanded?: boolean
}

const TONES: Record<
  AlertType,
  { box: string; chip: string; Icon: ComponentType<{ className?: string }> }
> = {
  info: {
    box: 'border-primary/20 bg-primary/[0.06]',
    chip: 'bg-primary/15 text-primary',
    Icon: Info
  },
  success: {
    box: 'border-primary/20 bg-primary/[0.06]',
    chip: 'bg-primary/15 text-primary',
    Icon: CircleCheck
  },
  warning: {
    box: 'border-peach/40 bg-peach/15',
    chip: 'bg-peach/30 text-warning',
    Icon: TriangleAlert
  },
  error: {
    box: 'border-destructive/25 bg-destructive/[0.06]',
    chip: 'bg-destructive/15 text-destructive',
    Icon: OctagonX
  }
}

export default function CollapsibleAlert({
  type = 'warning',
  showIcon = true,
  icon,
  style,
  className,
  label,
  description,
  isExpanded = false
}: CollapsibleAlertProps) {
  const [open, setOpen] = useState(isExpanded)
  const tone = TONES[type]

  return (
    <AccessControl role="USER">
      <div
        className={cn(
          'box-border w-full rounded-2xl border [&_*]:box-border',
          tone.box,
          className
        )}
        style={style}
      >
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex w-full select-none items-center gap-3 border-0 bg-transparent px-4 py-3 text-left"
        >
          {showIcon && (
            <span
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-full [&_svg]:h-5 [&_svg]:w-5',
                tone.chip
              )}
            >
              {icon ?? <tone.Icon />}
            </span>
          )}
          <span className="min-w-0 flex-1 text-sm font-semibold text-ink">
            {label}
          </span>
          <ChevronDown
            className={cn(
              'h-5 w-5 shrink-0 text-ink/50 transition-transform duration-300',
              open && 'rotate-180'
            )}
          />
        </button>

        <div
          className={cn(
            'grid transition-[grid-template-rows] duration-300 ease-out',
            open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          )}
        >
          <div className="min-h-0 overflow-hidden">
            <div
              className={cn(
                'pb-4 pr-4 text-sm text-ink/70',
                showIcon ? 'pl-[3.75rem]' : 'pl-4'
              )}
            >
              {description}
            </div>
          </div>
        </div>
      </div>
    </AccessControl>
  )
}
